/*
 * Copyright (c) 2023. American Express Company
 */

// theirs
import React from 'react';
import { LocalDate } from '@js-joda/core';
import {
    Alert,
    DateInput as DLSDateInput,
    FieldControl,
    Label,
    MonthInput,
    DayInput,
    YearInput
} from '@americanexpress/dls-react';
import { FieldHelperProps, FieldInputProps, FieldMetaProps, useField } from 'formik';

// ours
import { DateInputViewModel } from 'src/types/view-models/date-input.view.model';

// styles
import styles from './DateInput.module.scss';

export default function DateInput({ label, id, showDay }: DateInputViewModel): JSX.Element {
    const [, meta, helpers]: [FieldInputProps<LocalDate>, FieldMetaProps<LocalDate>, FieldHelperProps<LocalDate>] =
        useField(id);
    const hasError: boolean = meta.touched && !!meta.error;
    const { value }: { value: LocalDate } = meta;
    const {
        setValue,
        setTouched
    }: { setValue: (value: LocalDate) => void; setTouched: (value: boolean, shouldValidate?: boolean) => void } =
        helpers;

    // DLS requires we associate Alert with Input and Select in this particular way
    const alertId: string = `${id}Alert`;
    const describedBy: { describedBy?: string } = hasError ? { describedBy: alertId } : {};
    const describedby: { 'aria-describedby'?: string } = hasError ? { 'aria-describedby': alertId } : {};

    return (
        <FieldControl status={hasError ? 'error' : 'default'} data-cy={`${id}DateInput`}>
            <div className={styles.dateInputContainer}>
                <Label htmlFor={id}>{label}</Label>
                <DLSDateInput id={id} className={styles.monthYearContainer} onChange={() => {}} labelId={`${id}Label`}>
                    <MonthInput
                        id={`${id}-month`}
                        className={styles.monthContainer}
                        value={value?.monthValue() || 0}
                        onBlur={() => setTouched(true)}
                        onChange={(e: any) => {
                            e.persist();
                            // do not change the value if the user clicks "Select"
                            if (e.target.value > 0) {
                                setValue((value || LocalDate.now()).withMonth(e.target.value));
                            }
                        }}
                        {...describedby}
                    />
                    {showDay && (
                        <DayInput
                            id={`${id}-day`}
                            className={styles.yearContainer}
                            value={value?.dayOfMonth() || 0}
                            onBlur={() => setTouched(true)}
                            onChange={(e: any) => {
                                e.persist();
                                // do not change the value if the user clicks "Select"
                                if (e?.target?.value > 0) {
                                    setValue((value || LocalDate.now()).withDayOfMonth(e.target.value));
                                }
                            }}
                            {...describedby}
                        />
                    )}
                    <YearInput
                        id={`${id}-year`}
                        className={styles.yearContainer}
                        value={value?.year() || ''}
                        onBlur={() => {
                            setTouched(true);
                        }}
                        onChange={(e: any) => {
                            setValue((value || LocalDate.now()).withYear(e.target.value));
                        }}
                        {...describedBy}
                    />
                </DLSDateInput>
            </div>
            {hasError && (
                <Alert id={alertId} data-cy={`${id}DateInputError`}>
                    {meta.error}
                </Alert>
            )}
        </FieldControl>
    );
}
